import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface SchwarzschildCrossingPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  vec3 bg = mix(vec3(0.02, 0.04, 0.10), vec3(0.005, 0.01, 0.03), smoothstep(0.0, 0.7, r));
  vec3 col = bg;


  // We're falling into a black hole. The "altitude" decreases.
  float altitude = 1.5 - 0.4 * uTime * (0.3 + bass * 0.4);
  if (altitude < 0.3) altitude = 0.3;
  // The event horizon is at altitude = 0.5.
  float horizon = 0.5;

  // The "outside" universe appears as a compressed disc above us.
  // The disc subtends a larger angle as we get closer.
  float aperture = 0.3 + 0.3 / altitude;
  // "Up" is +y. Outside disc is around (0, +1).
  vec2 upDir = vec2(0.0, 1.0);
  float angDot = dot(p, upDir) / max(r, 0.001);
  // Inside the disc.
  bool inDisc = angDot > cos(aperture);
  // The disc shows the outside universe.
  if (inDisc) {
    // Sample a "starfield" for the outside universe.
    vec2 discUV = (p - upDir * dot(p, upDir)) / max(length(p - upDir * dot(p, upDir)), 0.001);
    // Stars.
    float stars = 0.0;
    for (int i = 0; i < 20; i++) {
      float fi = float(i);
      vec2 starPos = vec2(
        sin(fi * 1.7 + 0.5),
        cos(fi * 2.1 + 1.3)
      );
      float d = length(discUV - starPos * 0.7);
      stars += exp(-d * 80.0);
    }
    // The disc is bright blue (the outside universe is blueshifted
    // as we fall).
    vec3 discColor = vec3(0.5, 0.75, 1.0) * (1.0 + 1.5 * (1.0 - altitude / 1.5));
    col = mix(col, discColor * (0.3 + 0.7 * stars), inDisc ? 0.95 : 0.0);
  }

  // Below us: spaghettification lines pointing to the singularity.
  // The singularity is at the center of the screen.
  // Spaghetti lines: streaks that converge on the center.
  for (int i = 0; i < 12; i++) {
    float fi = float(i);
    // Each line starts at a point on the disc edge and goes to the center.
    float startX = (fi - 5.5) * 0.10;
    float startY = -0.10 - 0.05 * sin(fi * 2.3);
    vec2 startPt = vec2(startX, startY);
    vec2 endPt = vec2(0.0, 0.0);
    // Sample along the line.
    float minD = 1e9;
    for (int k = 0; k < 20; k++) {
      float fk = float(k) / 19.0;
      vec2 pt = mix(startPt, endPt, fk);
      // The line gets thinner toward the center.
      float d = length(p - pt);
      minD = min(minD, d);
    }
    float lineMask = exp(-minD * 100.0);
    col += vec3(0.95, 0.5, 0.3) * lineMask * 0.4;
  }

  // The "Einstein ring" — a bright halo at the apparent edge of the
  // black hole. For a Schwarzschild black hole, this ring has radius
  // proportional to the mass.
  float ringR = 0.15 + 0.05 * (1.0 - altitude / 1.5);
  float ringD = abs(r - ringR);
  float ringMask = exp(-ringD * 60.0);
  col += vec3(0.95, 0.85, 0.6) * ringMask * 0.7;

  // The central darkness (the "shadow" of the black hole).
  // The shadow grows as we approach the horizon.
  float shadowR = 0.10 + 0.10 * (1.0 - altitude / 1.0);
  if (r < shadowR) {
    col = mix(col, vec3(0.005, 0.005, 0.01), 0.95);
  }

  // The "blue sheet" — as altitude approaches 0, the outside disc
  // becomes ultra-bright.
  float blueSheet = (1.0 - altitude / 1.5) * 0.5 * (0.5 + 0.5 * high);
    if (inDisc) col += vec3(0.5, 0.85, 1.0) * blueSheet * 0.6;

  // The crossing flash: a bright pulse when we cross the horizon.
  float crossingFlash = exp(-abs(altitude - horizon) * 6.0);
  col += vec3(1.0, 0.95, 0.7) * crossingFlash * 0.7;

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;
  outColor = vec4(col, 1.0);
}
`;

export function computeSchwarzschildCrossingBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeSchwarzschildCrossingRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createSchwarzschildCrossingPipeline(gl: WebGL2RenderingContext): SchwarzschildCrossingPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawSchwarzschildCrossing(
  gl: WebGL2RenderingContext,
  pipe: SchwarzschildCrossingPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
